// @flow

import * as React from "react";

type Props = {|
  +children: React.Node,
  // Space kept free around the content, in px, so it never touches the screen's edges.
  +margin?: number,
|};

type State = {|
  scale: number,
  width: number,
  height: number,
|};

// Shrinks its content evenly (never enlarges it) so it fits inside the browser window without a scrollbar, on any screen
// size. Used by the plan modals (LegacyPlanModal, PremiumFeatureModal), which must always be fully visible: on a laptop
// screen they used to need a scroll, hiding the plan buttons. The content is laid out at its natural size and scaled
// with a CSS transform (which doesn't change its layout size, so measuring it can't feed back into the scale); the outer
// box takes the scaled size so the overlay centres it. Re-fits when the window or the content changes size.
class ScaleToFit extends React.Component<Props, State> {
    state = {scale: 1, width: 0, height: 0};

    inner: ?HTMLDivElement = null;
    observer: ?ResizeObserver = null;

    componentDidMount() {
        this.fit();
        window.addEventListener("resize", this.fit);
        if (typeof ResizeObserver !== "undefined" && this.inner) {
            this.observer = new ResizeObserver(this.fit);
            this.observer.observe(this.inner);
        }
    }

    // The content changed (e.g. the plans arrived and the cards appeared): measure again. A ResizeObserver alone missed
    // this in testing (the box kept the height it had before the plans loaded), so every re-render re-fits too; fit()
    // only sets state when something actually changed, so this can't loop.
    componentDidUpdate() {
        this.fit();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.fit);
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    fit = () => {
        const el = this.inner;
        if (!el) {
            return;
        }
        const margin = this.props.margin === undefined ? 24 : this.props.margin;
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        if (!width || !height) {
            return;
        }
        const scale = Math.min(1, (window.innerHeight - 2 * margin) / height, (window.innerWidth - 2 * margin) / width);
        if (scale !== this.state.scale || width !== this.state.width || height !== this.state.height) {
            this.setState({scale: Math.max(scale, 0.1), width, height});
        }
    };

    render(): React.Node {
        const {scale, width, height} = this.state;
        // Before the first measurement the box has no size of its own; the content shows at its natural size.
        const outerStyle = width && height ? {width: width * scale, height: height * scale} : {};
        return (
            <div className="scale-to-fit" style={outerStyle}>
                <div ref={(el) => { this.inner = el; }} className="scale-to-fit-inner"
                     style={{transform: scale < 1 ? `scale(${scale})` : "none"}}>
                    {this.props.children}
                </div>
            </div>
        );
    }
}

export default ScaleToFit;
